import React from 'react';
import type { AtlasCatalogItem } from '../../services/atlasService';
import AtlasDemoTags, { type AtlasDemoFamily } from './AtlasDemoTags';
import AtlasThumbnail from './AtlasThumbnail';

interface AtlasSelectionCardProps {
  family: string;
  title: string;
  atlases: AtlasCatalogItem[];
  demoFamily: AtlasDemoFamily;
  selectedAtlasId: string | null;
  expanded: boolean;
  onToggle: () => void;
  onSelect: (atlasId: string) => void;
  token?: string | null;
  selectionLocked?: boolean;
}

/** Select a family here; its contrast choices live in the viewer strip. */
export default function AtlasSelectionCard({
  family,
  title,
  atlases,
  demoFamily,
  selectedAtlasId,
  expanded,
  onToggle,
  onSelect,
  token,
  selectionLocked = false,
}: AtlasSelectionCardProps) {
  const selectedAtlas = atlases.find(atlas => atlas.id === selectedAtlasId);
  const atlas = selectedAtlas ?? atlases[0];
  const optionsId = `atlas-options-${encodeURIComponent(family)}`;
  const selectedBadge = (
    <span
      className="dicom-selected-tag"
      role="status"
      aria-label="Selected"
      title="Selected"
    >
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth={2.4}
        aria-hidden="true"
      >
        <path
          d="m5 12.5 4.25 4.25L19 7"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
      <span className="pc-visually-hidden">Selected</span>
    </span>
  );
  return (
    <div
      className={`dicom-series-item pc-atlas-family-card ${expanded ? 'selected' : ''} ${family === 'MNI' ? 'pc-atlas-featured-card' : ''}`}
      data-atlas-family={family}
      data-selected={Boolean(selectedAtlas)}
      role="button"
      tabIndex={0}
      aria-label={`${expanded ? 'Collapse' : 'Expand'} ${title}`}
      aria-expanded={expanded}
      aria-controls={expanded && !selectedAtlas ? optionsId : undefined}
      onClick={onToggle}
      onKeyDown={event => {
        if (event.target === event.currentTarget && (event.key === 'Enter' || event.key === ' ')) {
          event.preventDefault();
          onToggle();
        }
      }}
    >
      <div className="dicom-thumb">
        <AtlasThumbnail
          atlas={atlas}
          token={token}
        />
      </div>
      <div className="dicom-info">
        <div className="dicom-info-top">
          <span className="dicom-label">{title}</span>
        </div>
        <div className="dicom-meta">
          {atlas ? (
            <>
              <span>{atlas.space}</span>
              <span className="pc-atlas-preview-contrast">
                {atlas.contrast ?? 'Anatomical template'}
              </span>
              {atlas.data_kind && atlas.data_kind !== 'anatomical' && (
                <span title={atlas.description}>Subcortical atlas · not T1w/T2w MRI</span>
              )}
              {atlas.resolution_mm != null && <span>{atlas.resolution_mm} mm</span>}
              {expanded && atlas.dimensions && <span>{atlas.dimensions.join(' × ')} voxels</span>}
              {expanded && <span>{(atlas.template_size_bytes / (1024 * 1024)).toFixed(1)} MB</span>}
              {atlas.label_count > 0 && <span>{atlas.label_count} labels</span>}
            </>
          ) : (
            <span>Atlas data unavailable</span>
          )}
        </div>
      </div>
      <div className="dicom-action">
        {expanded && !selectedAtlas && (
          <button
            type="button"
            className="modal-submit-button modal-submit-icon-button dicom-expanded-create-button"
            id={optionsId}
            data-atlas-id={atlas?.id}
            aria-label={`Select ${title}`}
            disabled={!atlas || selectionLocked}
            onClick={event => {
              event.stopPropagation();
              if (atlas && !selectionLocked) onSelect(atlas.id);
            }}
          >
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth={2}
              aria-hidden="true"
            >
              <path
                d="m5 12.5 4.25 4.25L19 7"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <span className="pc-visually-hidden">Select</span>
          </button>
        )}
        {expanded && selectedAtlas && selectedBadge}
        <div className="dicom-classification-stack">
          {atlas?.data_kind && atlas.data_kind !== 'anatomical' ? (
            <>
              <span className="dicom-match-label match-uncertain">Research atlas</span>
              <span className="dicom-modality-badge modality-unknown">
                {atlas.data_kind === 'labelmap' ? 'Label map' : 'Probability'}
              </span>
            </>
          ) : (
            <AtlasDemoTags family={demoFamily} />
          )}
        </div>
      </div>
      {!expanded && selectedAtlas && (
        <span className="pc-atlas-collapsed-selected">{selectedBadge}</span>
      )}
    </div>
  );
}
